import { useQuery } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { type Classifier as LoadedClassifier, parseModel } from './model'

export type ClassificationStatus = 'loading' | 'ready' | 'unavailable'

export interface ClassifiedLabel {
	name: string
	/** The model's display name for the label, in the model's language. */
	title: string
	score: number
	/** The score at which the model counts this label as present. */
	threshold: number
	/** The score is at the threshold or above, and the text has the label's minimum number of words. */
	detected: boolean
}

const DEFAULT_THRESHOLD = 0.5

export interface Classification<Label extends string = string> {
	/** In the model's label order. */
	labels: ClassifiedLabel[] | null
	scores: Record<Label, number> | null
	status: ClassificationStatus
}

const ModelsUrlContext = createContext<string | null | undefined>(undefined)
const ScoresContext = createContext<Record<string, number> | null>(null)

export function ClassifierProvider({ url, children }: { url: string | undefined; children: ReactNode }) {
	return (
		<ModelsUrlContext.Provider value={url ? url.replace(/\/+$/, '') : null}>{children}</ModelsUrlContext.Provider>
	)
}

export interface ClassificationOptions {
	/**
	 * How long, in milliseconds, the contents must stay unchanged before a half-typed last
	 * word is scored. Until then it is left out, because it shares its start with longer
	 * words ("engin" in "engineer"). 0 scores the contents exactly as given.
	 */
	settleDelay?: number
}

const DEFAULT_SETTLE_DELAY = 600

export function useClassification<Label extends string = string>(
	model: string,
	language: string,
	text: string,
	{ settleDelay = DEFAULT_SETTLE_DELAY }: ClassificationOptions = {},
): Classification<Label> {
	const contents = useSettledText(text, settleDelay)
	const modelsUrl = useContext(ModelsUrlContext)

	useEffect(() => {
		if (modelsUrl === undefined) {
			console.error('useClassification is used outside a ClassifierProvider, so nothing is classified.')
		}
	}, [modelsUrl])

	const url = `${modelsUrl}/${model}.${language}.bin`
	const query = useQuery({
		queryKey: ['classifier', url],
		queryFn: () => fetchClassifier(url),
		staleTime: Infinity,
		enabled: typeof modelsUrl === 'string',
	})
	const classifier = query.data ?? null

	const scores = useMemo(() => (classifier ? classifier.score(contents) : null), [classifier, contents])
	const labels = useMemo(
		() => {
			if (!classifier || !scores) {
				return null
			}

			const wordCount = classifier.wordCount(contents)

			return classifier.labels.map((name) => {
				const threshold = classifier.thresholds[name] ?? DEFAULT_THRESHOLD

				return {
					name,
					title: classifier.titles[name] ?? name,
					score: scores[name],
					threshold,
					detected: scores[name] >= threshold && wordCount >= (classifier.minWords[name] ?? 1),
				}
			})
		},
		[classifier, scores, contents],
	)

	return {
		labels,
		scores: scores as Record<Label, number> | null,
		status: classificationStatus(query.isLoading, classifier),
	}
}

function useSettledText(text: string, settleDelay: number): string {
	const [settledText, setSettledText] = useState(text)

	useEffect(() => {
		if (settleDelay === 0) {
			return
		}

		const timer = setTimeout(() => setSettledText(text), settleDelay)

		return () => clearTimeout(timer)
	}, [text, settleDelay])

	return settleDelay === 0 || text === settledText ? text : text.replace(/\S+$/u, '')
}

function classificationStatus(isLoading: boolean, classifier: LoadedClassifier | null): ClassificationStatus {
	if (isLoading) {
		return 'loading'
	}

	return classifier === null ? 'unavailable' : 'ready'
}

/**
 * Resolves to null when there is no model at the URL, so a language without a trained
 * model shows no hints instead of failing.
 */
async function fetchClassifier(url: string): Promise<LoadedClassifier | null> {
	const response = await fetch(url)

	if (response.status === 404) {
		return null
	}

	if (!response.ok) {
		throw new Error(`Could not load the classifier model from ${url}: ${response.status}.`)
	}

	return parseModel(await response.arrayBuffer())
}

interface ClassifierProps extends ClassificationOptions {
	model: string
	language: string
	contents: string
	children: ReactNode | ((labels: ClassifiedLabel[]) => ReactNode)
}

/**
 * Renders nothing until the model has loaded, or at all when there is no model for the
 * language.
 */
function ClassifierRoot({ model, language, contents, settleDelay, children }: ClassifierProps) {
	const { labels, scores } = useClassification(model, language, contents, { settleDelay })

	if (labels === null || scores === null) {
		return null
	}

	return (
		<ScoresContext.Provider value={scores}>
			{typeof children === 'function' ? children(labels) : children}
		</ScoresContext.Provider>
	)
}

interface LabelProps {
	name: string
	/** Inclusive lower bound of the score. */
	min?: number
	/** Exclusive upper bound, so `min={0.75}` and `max={0.75}` never both match. */
	max?: number
	children: ReactNode
}

function ClassifierLabel({ name, min, max, children }: LabelProps) {
	const scores = useContext(ScoresContext)
	const score = scores?.[name]

	if (score === undefined || (min !== undefined && score < min) || (max !== undefined && score >= max)) {
		return null
	}

	return <>{children}</>
}

export const Classifier = Object.assign(ClassifierRoot, { Label: ClassifierLabel })
